<div class="table-toolbar">

  <!-- Left: optional title -->
  <div class="toolbar-left">
    <ng-content select="[toolbarTitleActions]"></ng-content>
    <div *ngIf="title" class="toolbar-title" [attr.aria-label]="titleAriaLabel || title">
      <mat-icon *ngIf="titleIcon" class="toolbar-title__icon">{{ titleIcon }}</mat-icon>
      <span class="toolbar-title__text">{{ title }}</span>
      <button
        *ngIf="titleInfoTooltip"
        type="button"
        mat-icon-button
        class="toolbar-title__info"
        [matTooltip]="titleInfoTooltip"
        [attr.aria-label]="titleInfoTooltip"
        (click)="titleInfoClick.emit()">
        <mat-icon>info_outline</mat-icon>
      </button>
    </div>
  </div>

  <!-- Right: search + filter + column visibility + density + export -->
  <div class="toolbar-right">

    <!-- Search toggle + expandable input -->
    <div *ngIf="enableSearch" class="search-wrapper" [class.search-wrapper--open]="searchOpen">
      <button
        mat-icon-button
        class="toolbar-btn search-btn"
        [class.active]="searchOpen || hasActiveSearch"
        (click)="toggleSearch()"
        [matTooltip]="searchOpen ? 'Cerrar búsqueda' : 'Buscar'">
        <mat-icon>search</mat-icon>
      </button>

      <div class="search-input-wrapper" [class.search-input-wrapper--open]="searchOpen">
        <input
          class="search-input"
          [placeholder]="searchPlaceholder"
          [value]="searchValue"
          (input)="onSearchInput($any($event.target).value)"
          (keydown.escape)="clearSearch()"
          #searchInputEl
          autocomplete="off" />
        <mat-spinner
          *ngIf="isLoading && searchOpen"
          class="search-loading-spinner"
          diameter="18"
          aria-label="Buscando">
        </mat-spinner>
        <button *ngIf="searchValue" mat-icon-button class="search-clear-btn" (click)="clearSearch()" tabindex="-1">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Sort menu -->
    <button
      *ngIf="enableSort && sortableColumns.length > 0"
      mat-icon-button
      class="toolbar-btn sort-btn"
      [class.active]="hasActiveSort"
      [matMenuTriggerFor]="sortMenu"
      matTooltip="Ordenar">
      <mat-icon>sort</mat-icon>
    </button>

    <mat-menu #sortMenu="matMenu" class="sort-menu">
      <div class="column-menu-header">Ordenar por</div>
      <button
        mat-menu-item
        *ngFor="let col of sortableColumns; trackBy: trackColumn"
        [class.active-sort-option]="sortState?.sortBy === col.key"
        (click)="selectSortColumn(col)">
        <mat-icon class="sort-option-check">{{ getSortIcon(col) }}</mat-icon>
        <span>{{ col.header }}</span>
      </button>
    </mat-menu>

    <!-- Filter panel toggle -->
    <button
      *ngIf="enableFilter && !hasFilterOptions"
      mat-icon-button
      class="toolbar-btn filter-btn"
      [class.active]="filterPanelOpen || activeFilters.length > 0"
      (click)="toggleFilterPanel()"
      matTooltip="Filtros">
      <mat-icon>filter_list</mat-icon>
      <span *ngIf="activeFilters.length > 0" class="filter-badge">{{ activeFilters.length }}</span>
    </button>

    <!-- Filter menu -->
    <button
      *ngIf="enableFilter && hasFilterOptions"
      mat-icon-button
      class="toolbar-btn filter-btn"
      [class.active]="activeFilters.length > 0"
      [matMenuTriggerFor]="filterMenu"
      matTooltip="Filtros">
      <mat-icon>filter_list</mat-icon>
      <span *ngIf="activeFilters.length > 0" class="filter-badge">{{ activeFilters.length }}</span>
    </button>

    <mat-menu #filterMenu="matMenu" class="filter-menu">
      <ng-container *ngFor="let group of filterMenuGroups; let first = first; trackBy: trackFilterGroup">
        <div class="column-menu-header" [class.filter-group-header--spaced]="!first">{{ group.label }}</div>
        <button
          mat-menu-item
          *ngFor="let option of group.options"
          [class.active-filter-option]="option.active"
          (click)="selectFilterOption(option, $event)">
          <mat-icon class="filter-option-check">{{ option.active ? 'check' : '' }}</mat-icon>
          <span>{{ option.label }}</span>
        </button>
      </ng-container>
    </mat-menu>

    <!-- Column visibility -->
    <button
      *ngIf="enableColumnVisibility && hideableColumns.length > 0"
      mat-icon-button
      class="toolbar-btn column-visibility-btn"
      [matMenuTriggerFor]="columnMenu"
      matTooltip="Columnas">
      <mat-icon>view_column</mat-icon>
    </button>

    <mat-menu #columnMenu="matMenu" class="column-menu">
      <div class="column-menu-header">Columnas visibles</div>
      <button
        mat-menu-item
        *ngFor="let col of hideableColumns"
        (click)="$event.stopPropagation(); toggleColumn(col.key, !isColumnVisible(col.key))">
        <mat-checkbox
          [checked]="isColumnVisible(col.key)"
          (change)="toggleColumn(col.key, $event.checked)"
          (click)="$event.stopPropagation()">
          {{ col.header }}
        </mat-checkbox>
      </button>
    </mat-menu>

    <!-- Density cycle button -->
    <button
      *ngIf="enableDensity && viewOptions.length <= 1"
      mat-icon-button
      class="toolbar-btn density-btn"
      (click)="cycleDensity()"
      [matTooltip]="densityLabels[currentDensity]">
      <mat-icon>{{ currentDensity === 'compact' ? 'density_small' : currentDensity === 'comfortable' ? 'density_large' : 'density_medium' }}</mat-icon>
    </button>

    <button
      *ngIf="viewOptions.length > 1"
      mat-icon-button
      type="button"
      class="toolbar-btn view-menu-btn"
      aria-label="Cambiar vista"
      [class.active]="activeViewOption"
      [matMenuTriggerFor]="viewMenu"
      [matTooltip]="activeViewOption?.label || 'Vista'">
      <mat-icon>{{ activeViewOption?.icon || 'table_rows' }}</mat-icon>
    </button>

    <mat-menu #viewMenu="matMenu" class="view-menu">
      <div class="column-menu-header">Vista</div>
      <button
        *ngFor="let option of viewOptions"
        mat-menu-item
        type="button"
        [class.active-view-option]="activeView === option.value"
        (click)="selectView(option.value)">
        <mat-icon class="view-option-check">{{ activeView === option.value ? 'check' : '' }}</mat-icon>
        <mat-icon>{{ option.icon }}</mat-icon>
        <span>{{ option.label }}</span>
      </button>
      <ng-container *ngIf="enableDensity">
        <div class="column-menu-header filter-group-header--spaced">Densidad</div>
        <button
          *ngFor="let option of densityOptions"
          mat-menu-item
          type="button"
          [class.active-density]="currentDensity === option.value"
          (click)="setDensity(option.value)">
          <mat-icon class="view-option-check">{{ currentDensity === option.value ? 'check' : '' }}</mat-icon>
          <mat-icon>{{ option.icon }}</mat-icon>
          <span>{{ option.label }}</span>
        </button>
      </ng-container>
    </mat-menu>

    <!-- Export -->
    <button
      *ngIf="enableExport"
      mat-icon-button
      class="toolbar-btn"
      (click)="onExport()"
      matTooltip="Exportar CSV">
      <mat-icon>download</mat-icon>
    </button>

    <ng-content select="[toolbarActions]"></ng-content>

    <button
      *ngIf="canShowPrimaryAction && primaryActionOptions.length === 0"
      mat-raised-button
      color="primary"
      class="toolbar-primary-action"
      type="button"
      [attr.aria-label]="primaryActionLabel"
      (click)="onPrimaryAction()">
      <span class="toolbar-primary-action__desktop-label">{{ primaryActionLabel }}</span>
      <span class="toolbar-primary-action__mobile-label">{{ primaryActionMobileText }}</span>
    </button>

    <button
      *ngIf="canShowPrimaryAction && primaryActionOptions.length > 0"
      mat-raised-button
      color="primary"
      class="toolbar-primary-action"
      type="button"
      [matMenuTriggerFor]="primaryActionMenu">
      <span class="toolbar-primary-action__content">
        <span>{{ primaryActionLabel }}</span>
        <mat-icon class="toolbar-primary-action__chevron">arrow_drop_down</mat-icon>
      </span>
    </button>

    <mat-menu #primaryActionMenu="matMenu">
      <button
        mat-menu-item
        *ngFor="let option of primaryActionOptions"
        type="button"
        (click)="onPrimaryActionOption(option)">
        <mat-icon *ngIf="option.icon">{{ option.icon }}</mat-icon>
        <span>{{ option.label }}</span>
      </button>
    </mat-menu>

  </div>

</div>

<div class="quick-filter-wrapper">
  <ng-content select="[quickFilterPanel]"></ng-content>
</div>

<!-- Collapsible filter panel (content projected from host) -->
<div
  *ngIf="enableFilter && !hasFilterOptions"
  class="filter-panel-wrapper"
  [class.filter-panel-wrapper--open]="filterPanelOpen">
  <div class="filter-panel-content">
    <ng-content select="[filterPanel]"></ng-content>
    <button
      *ngIf="enableClearFilters"
      type="button"
      mat-button
      class="clear-filter-btn"
      (click)="clearFiltersClick.emit()">
      <mat-icon>clear_all</mat-icon>
      Limpiar
    </button>
  </div>
</div>
